import { Address } from "@common/eircode/Address";
import { IUserAddress } from "@common/eircode/Interface/IAddressForm";
import { Loader } from "@common/handleErrorAndLoading/Loader";
import { Modal } from "@common/Modal/Modal";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { Fragment, useState } from "react";
import { Controller } from "react-hook-form";
import { useTranslation, Trans } from "react-i18next";
import { IUserDetailsInfo } from "../Profile/interface/IUserDetails";
import { useReplacementSimForm } from "./hook/useReplacementSimForm";
import { getLanguage } from "@utils/changeLanguage";
import PhoneInput from "react-phone-input-2";
import "../Profile/UserDetails/phoneInputFiles/phoneInput.scss";
import gr from "../Profile/UserDetails/phoneInputFiles/lang/gr.json";
import { PhoneNumberUtil } from "google-libphonenumber";

interface IParams {
    closeForm(): void;
    displaySnackbar(): void;
    deliveryAddress?: IUserAddress;
    userDetails?: IUserDetailsInfo;
    getSimReplacePending: () => void;
}

const phoneUtil = PhoneNumberUtil.getInstance();

export const ReplacementSimAddressForm = (props: IParams) => {
    const { closeForm, displaySnackbar, userDetails, deliveryAddress, getSimReplacePending } = props;
    const [isMobileNumberValid, setIsMobileNumberValid] = useState<boolean>(true);
    const { t } = useTranslation(["sim", "common", "glossary", "profile", "validation"]);

    const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);

    const openErrorModal = () => setErrorModalOpen(true);
    const closeErrorModal = () => setErrorModalOpen(false);

    const onFormSubmitted = () => {
        closeForm();
        displaySnackbar();
        getSimReplacePending();
    };

    const {
        postReplacementSimState,
        barPreviousSim,
        postReplacementSimInitial,
        setBarPreviousSim,
        formMethods,
    } = useReplacementSimForm(deliveryAddress, userDetails);

    const handleSubmit = formMethods.handleSubmit(() => {
        postReplacementSimInitial(openErrorModal, onFormSubmitted);
    });

    const { errors, control, clearErrors } = formMethods;

    const validatePhoneNumber = (number: string) => {
        let error: string | undefined = undefined;

        try {
            const num = "+".concat(number);
            if (!phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(num))) {
                error = t("profile:phoneNumberError");
            }
        } catch (e: any) {
            error = e.message;
        }

        error?.length ? setIsMobileNumberValid(false) : setIsMobileNumberValid(true);
        return error;
    };

    const onMobileNumberChange = () => {
        if (errors.mobileNumber) clearErrors("mobileNumber");
    };

    return (
        <Fragment>
            {errorModalOpen && (
                <Modal
                    close={closeErrorModal}
                    confirm={closeErrorModal}
                    confirmLabel={t("glossary:close")}
                    content={t("sim:replacementSimError")}
                    isModalOpen={errorModalOpen}
                    title={t("glossary:error")}
                />
            )}
            <Typography color="secondary" variant="h5" style={{ marginBottom: "15px" }}>
                {t("sim:provideDetails")}
            </Typography>
            <form data-testid="order-sim-form" onSubmit={handleSubmit}>
                <Grid item md={4} sm={6} xs={12} style={{ marginBottom: "15px" }}>
                    <Controller
                        control={control}
                        name="mobileNumber"
                        render={(controllerProps) => (
                            <PhoneInput
                                disabled={postReplacementSimState.isLoading}
                                country={"cy"}
                                preferredCountries={["cy"]}
                                countryCodeEditable={false}
                                localization={getLanguage() === "el" ? gr : undefined}
                                enableSearch={true}
                                value={controllerProps.value}
                                specialLabel={`${t("profile:contactNumber")} *`}
                                searchPlaceholder={t("profile:searchCountry")}
                                searchNotFound={t("profile:noCountryFound")}
                                onChange={(phone: any) => {
                                    onMobileNumberChange();
                                    controllerProps.onChange(phone);
                                }}
                                isValid={() => {
                                    return isMobileNumberValid;
                                }}
                            />
                        )}
                        rules={{
                            required: t("validation:required"),
                            validate: (val: string) => validatePhoneNumber(val),
                        }}
                    />
                    {formMethods.errors.mobileNumber && (
                        <Typography color="error" variant="body1">
                            {formMethods.errors.mobileNumber.message}
                        </Typography>
                    )}
                    <Typography color="textSecondary" gutterBottom id="mobileOtpHelper" variant="body2">
                        {t("sim:mobileSimChangeHelper")}
                    </Typography>
                </Grid>
                <Typography align="center" color="secondary" gutterBottom variant="h3">
                    {t("sim:deliveryAddress")}
                </Typography>
                <Divider light style={{ marginBottom: "20px" }} variant="middle" />
                <Address addressForm={formMethods} isEditing={false} isLoading={false} prefix={"billingAddress"} />
                <Grid className="replacementSim-blockService" container>
                    <Grid item xs={12}>
                        <Typography align="center" color="secondary" gutterBottom variant="h3">
                            {t("sim:blockService")}
                        </Typography>
                    </Grid>
                    <Grid className="replacementSim__divider" item xs={12}>
                        <Divider light variant="middle" />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography align="center" variant="body1">
                            <Trans i18nKey="blockMyServicesHelper" ns="sim" />
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={barPreviousSim}
                                    color="secondary"
                                    style={{ padding: "10px 4px 15px 12px" }}
                                    onChange={() => setBarPreviousSim(!barPreviousSim)}
                                />
                            }
                            disabled={postReplacementSimState.isLoading}
                            label={t("sim:serviceBlockCheckbox")}
                        />
                    </Grid>
                </Grid>
                <Grid container justify="center" spacing={2}>
                    {postReplacementSimState.isLoading && (
                        <Grid item sm={2} xs={4}>
                            <Loader size={40} />
                        </Grid>
                    )}
                    {!postReplacementSimState.isLoading && (
                        <Fragment>
                            <Grid item>
                                <Button color="secondary" size="large" variant="outlined" onClick={closeForm}>
                                    {t("common:cancel")}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button color="secondary" id="submit" size="large" type="submit" variant="contained">
                                    {t("sim:sendNewSim")}
                                </Button>
                            </Grid>
                        </Fragment>
                    )}
                </Grid>
            </form>
        </Fragment>
    );
};
